/**
 * @fileoverview Repository for LogPlanCentroCosto collection operations
 * Implements repository pattern for task queue percentage fix functionality
 */

import { DataRepository } from './base/DataRepository';
import { 
  LogPlanCentroCosto, 
  CreateLogPlanCentroCostoData, 
  UpdateLogPlanCentroCostoData,
  LOG_PLAN_CENTRO_COSTO_COLLECTION 
} from '../types/collections/LogPlanCentroCosto';
import { RepositoryError } from './base/DataRepository';

/**
 * Interface for filters used in report queries for operarios
 */
export interface ReporteOperariosFilters {
  operario: string | null;
  proveedor: string | null;
  orden: string | null;
  centroCosto: string | null;
  fechaInicio: { inicio: string | null; fin: string | null };
  fechaFin: { inicio: string | null; fin: string | null };
}

/**
 * Repository class for LogPlanCentroCosto collection
 * Provides specialized methods for task queue percentage fix functionality
 */
export class LogPlanCentroCostoRepository extends DataRepository<LogPlanCentroCosto> {
  constructor() {
    super(LOG_PLAN_CENTRO_COSTO_COLLECTION.name);
  }

  /**
   * Get percentage from a log record
   * @param logId - ID of the log record
   * @returns Percentage value (0-100) or 0 if undefined
   */
  async getLogPercentage(logId: string): Promise<number> {
    const log = await this.getById(logId);
    return log.porcentaje || 0;
  }

  /**
   * Find next task in plan sequence
   * @param ordenId - Work order ID
   * @param currentOrdenPlan - Current task's orden_plan
   * @returns Next task in sequence or null if not found
   */
  async findNextTaskInPlan(ordenId: string, currentOrdenPlan: number): Promise<LogPlanCentroCosto | null> {
    // Find the next task with orden_plan > currentOrdenPlan, ordered by orden_plan
    const tasks = await this.getList(1, 1, {
      filter: `orden = "${ordenId}" && orden_plan > ${currentOrdenPlan} && (fin = "" || fin = null) && ready = false`,
      sort: 'orden_plan'
    });
    
    return tasks.items.length > 0 ? tasks.items[0] : null;
  }

  /**
   * Get all tasks for a work order sorted by orden_plan
   * @param ordenId - Work order ID
   * @returns Array of log records sorted by orden_plan
   */
  async getTasksForOrder(ordenId: string): Promise<LogPlanCentroCosto[]> {
    return this.getAll({
      filter: `orden = "${ordenId}"`,
      sort: 'orden_plan'
    });
  }

  /**
   * Update task priority (user-controlled)
   * @param logId - ID of the log record
   * @param newPriority - New priority value
   * @returns Updated log record
   */
  async updateTaskPriority(logId: string, newPriority: number): Promise<LogPlanCentroCosto> {
    return this.update(logId, { prioridad: newPriority });
  }

  /**
   * Activate task by setting ready and priority to 1 (without starting it)
   * @param logId - ID of the log record
   * @returns Updated log record
   */
  async activateTask(logId: string): Promise<LogPlanCentroCosto> {
    return this.update(logId, {
      ready: true,
      prioridad: 1
    });
  }

  /**
   * Update task priority to 1 (make it the next task to be started)
   * @param logId - ID of the log record
   * @returns Updated log record
   */
  async promoteTaskToPriority1(logId: string): Promise<LogPlanCentroCosto> {
    return this.update(logId, {
      prioridad: 1
    });
  }

  /**
   * Create new log record
   * @param data - Log data
   * @returns Created log record
   */
  async createLog(data: CreateLogPlanCentroCostoData): Promise<LogPlanCentroCosto> {
    return this.create(data);
  }

  /**
   * Update existing log record
   * @param logId - ID of the log record
   * @param data - Update data
   * @returns Updated log record
   */
  async updateLog(logId: string, data: UpdateLogPlanCentroCostoData): Promise<LogPlanCentroCosto> {
    return this.update(logId, data);
  }

  /**
   * Get tasks by orden_plan range for a work order
   * @param ordenId - Work order ID
   * @param minOrdenPlan - Minimum orden_plan value
   * @param maxOrdenPlan - Maximum orden_plan value
   * @returns Array of log records in the range
   */
  async getTasksByOrdenPlanRange(
    ordenId: string, 
    minOrdenPlan: number, 
    maxOrdenPlan: number
  ): Promise<LogPlanCentroCosto[]> {
    return this.getAll({
      filter: `orden = "${ordenId}" && orden_plan >= ${minOrdenPlan} && orden_plan <= ${maxOrdenPlan}`,
      sort: 'orden_plan'
    });
  }

  /**
   * Get ready tasks for a work order
   * @param ordenId - Work order ID
   * @returns Array of ready log records
   */
  async getReadyTasks(ordenId: string): Promise<LogPlanCentroCosto[]> {
    return this.getAll({
      filter: `orden = "${ordenId}" && ready = true`,
      sort: 'orden_plan'
    });
  }

  /**
   * Get completed tasks for a work order
   * @param ordenId - Work order ID
   * @returns Array of completed log records
   */
  async getCompletedTasks(ordenId: string): Promise<LogPlanCentroCosto[]> {
    return this.getAll({
      filter: `orden = "${ordenId}" && fin != ""`,
      sort: 'orden_plan'
    });
  }

  /**
   * Get a specific task by orden_plan for a work order
   * @param ordenId - Work order ID
   * @param ordenPlan - orden_plan value
   * @returns Log record or null if not found
   */
  async getTaskByOrdenPlan(ordenId: string, ordenPlan: number): Promise<LogPlanCentroCosto | null> {
    return this.findFirst(`orden = "${ordenId}" && orden_plan = ${ordenPlan}`);
  }

  /**
   * Get logs for report with filters for operarios, proveedores, ordenes, centros de costo and dates
   * @param filters - Filter criteria for the report
   * @returns Array of log records with expanded relations
   */
  async getLogsParaReporte(filters: ReporteOperariosFilters): Promise<LogPlanCentroCosto[]> {
    try {
      const filterParts: string[] = [];

      // Filter by operario
      if (filters.operario) {
        filterParts.push(`operario = "${filters.operario}"`);
      }

      // Filter by proceso_proveedor
      if (filters.proveedor) {
        filterParts.push(`proceso_proveedor = "${filters.proveedor}"`);
      }

      // Filter by orden
      if (filters.orden) {
        filterParts.push(`orden = "${filters.orden}"`);
      }

      // Filter by centro_costo
      if (filters.centroCosto) {
        filterParts.push(`centro_costo = "${filters.centroCosto}"`);
      }

      // Filter by fechaInicio range
      if (filters.fechaInicio.inicio && filters.fechaInicio.fin) {
        filterParts.push(`inicio >= "${filters.fechaInicio.inicio}" && inicio <= "${filters.fechaInicio.fin}"`);
      }

      // Filter by fechaFin range
      if (filters.fechaFin.inicio && filters.fechaFin.fin) {
        filterParts.push(`fin >= "${filters.fechaFin.inicio}" && fin <= "${filters.fechaFin.fin}"`);
      }

      const filter = filterParts.join(' && ');

      // Get logs with expanded relations
      const logs = await this.pb.collection(this.collectionName).getFullList<LogPlanCentroCosto>({
        filter,
        expand: 'orden,centro_costo,operario,proceso_proveedor',
        sort: '-created'
      });

      return logs;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener logs para reporte: ${error instanceof Error ? error.message : String(error)}`,
        'GET_LOGS_REPORTE_ERROR'
      );
    }
  }
}



